// src/features/developer/components/JwtPartEditor.tsx
import React, { useState } from 'react';
import {
    Box, Paper, Typography, Tabs, Tab, Tooltip, IconButton, TableContainer, Table, TableBody, TableRow, TableCell, Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import CodeEditor from '../../developer/components/CodeEditor'; // Reusing existing one
import { JWT_CLAIM_DESCRIPTIONS } from '../constants/jwtConstants';

interface JwtPartEditorProps {
    title: string;
    data: Record<string, any> | null;
    rawData: string;
    onRawDataChange: (value: string | undefined) => void;
    readOnly?: boolean;
}

const DataRow: React.FC<{ claim: string; value: any }> = ({ claim, value }) => {
    const { t } = useTranslation();
    const isTimestamp = ['iat', 'exp', 'nbf'].includes(claim) && typeof value === 'number';
    const descriptionKey = JWT_CLAIM_DESCRIPTIONS[claim];

    const formattedValue = isTimestamp ? format(new Date(value * 1000), "yyyy-MM-dd'T'HH:mm:ss'Z'") : JSON.stringify(value);

    const keyCell = (
        <TableCell sx={{ fontWeight: 500, border: 'none', py: 1, verticalAlign: 'top', width: '20%' }}>
            {claim}
        </TableCell>
    );

    return (
        <TableRow>
            {descriptionKey ? (
                <Tooltip title={t(descriptionKey, { ns: 'tools'})} placement="left" arrow>
                    {keyCell}
                </Tooltip>
            ) : (
                keyCell
            )}
            <TableCell sx={{ border: 'none', py: 1, wordBreak: 'break-all' }}>
                {isTimestamp ? (
                    <Tooltip title="Coordinated Universal Time (UTC)" placement="top" arrow>
                        <Chip label={formattedValue} size="small" variant="outlined" />
                    </Tooltip>
                ) : (
                    formattedValue
                )}
            </TableCell>
        </TableRow>
    );
};


const JwtPartEditor: React.FC<JwtPartEditorProps> = ({ title, data, rawData, onRawDataChange, readOnly = false }) => {
    const { t } = useTranslation();
    const [view, setView] = useState<'json' | 'data'>('json');
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(rawData);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleClear = () => {
        onRawDataChange('');
    };

    return (
        <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'action.hover'
            }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {title}
                </Typography>
                <Box>
                    <Tooltip title={copied ? t('jwt_tool_copied', { ns: 'tools'}) : t('jwt_tool_copy', { ns: 'tools'})}>
                        <IconButton size="small" onClick={handleCopy}><ContentCopyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {!readOnly && (
                        <Tooltip title={t('jwt_tool_clear', { ns: 'tools'})}>
                            <IconButton size="small" onClick={handleClear}><ClearIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={view} onChange={(e, newValue) => setView(newValue)} variant="fullWidth">
                    <Tab label={t('jwt_tool_json_view', { ns: 'tools'})} value="json" />
                    <Tab label={t('jwt_tool_data_view', { ns: 'tools'})} value="data" disabled={!data} />
                </Tabs>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
                {view === 'json' && (
                    // Using a wrapper div to ensure editor fills the space
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                        <CodeEditor
                            value={rawData}
                            onChange={onRawDataChange}
                            language="json"
                            readOnly={readOnly}
                            // Adjust placeholder if needed
                        />
                    </Box>
                )}
                {view === 'data' && data && (
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                {Object.entries(data).map(([key, value]) => (
                                    <DataRow key={key} claim={key} value={value} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Paper>
    );
};

export default JwtPartEditor;